import {
  COLLAB_API_BASE,
  collabFetch,
  consumeStudentTokenFromFragment,
  getStudentTokenKind,
  loadRuntimeTrustedOrigins,
  requestStudentTokenFromParent,
  setStudentToken,
} from "./auth";

export interface DerivedPseudonymParams {
  derivedPin: string;
  pseudonymId: string;
}

export interface AnonymousEmbeddedParams {
  pin: string | null;
  isTeacher: boolean;
  sessionId: string | null;
  classId: string | null;
}

function readDerivedPin(search: string): string | null {
  const params = new URLSearchParams(search);
  return (
    params.get("app_pin")?.trim() ||
    params.get("derived_pin")?.trim() ||
    params.get("tl4k_pin")?.trim() ||
    null
  );
}

export function readDerivedPseudonymParams(search: string): DerivedPseudonymParams | null {
  const params = new URLSearchParams(search);
  const derivedPin = readDerivedPin(search);
  // L'identita' arriva con due nomi diversi e la funzione ne leggeva uno solo.
  // TechyWeb scrive `tl4k_identity` (apps/techyweb/utils/tl4kSessionParams.ts,
  // TL4K_PARAM_IDENTITY); `pseudonym_id` e' il nome interno di alcune app.
  // Leggendo solo quello, il bootstrap tornava null per ogni studente che
  // entrava dal launcher, e l'app cadeva sul ramo admin: schermata di login,
  // nessuna rotta chiamata, zero errori in console. Il sintomo e' "si apre ma
  // non funziona", che e' il peggiore da diagnosticare in aula.
  const pseudonymId =
    params.get("pseudonym_id")?.trim() ||
    params.get("tl4k_identity")?.trim() ||
    null;
  if (!derivedPin || !pseudonymId) return null;
  return { derivedPin, pseudonymId };
}

// Il bootstrap legge il PIN solo dall'URL (app_pin > derived_pin > tl4k_pin). Se lo
// studente apre l'app PRIMA che il docente abbia creato la sessione figlia, TechyWeb
// passa solo il PIN dell'incontro: resolveAppSessionLaunch aspetta e restituisce il
// PIN figlio, ma se si passa al bootstrap la query originale lo studente entra
// nell'incontro e salva gli artefatti sul genitore. Qui si impone il PIN risolto.
export function withResolvedAppPin(search: string, appPin: string | null | undefined): string {
  const pin = appPin?.trim();
  if (!pin) return search;
  const params = new URLSearchParams(search);
  params.set("app_pin", pin);
  return `?${params.toString()}`;
}

export async function bootstrapEmbeddedStudentSession<T>(
  search: string,
  mapJoinData: (payload: any) => T,
  apiBase: string = COLLAB_API_BASE,
  retries: number = 2,
): Promise<T | null> {
  // WP-13e: legge il frammento `#tl4k_st=` (se c'e') PRIMA di tutto, cosi' il token del padre e'
  // gia' in archivio quando collabFetch lo manda come `X-TL4K-Student` (spec 5.4) e il frammento
  // sparisce dalla barra.
  consumeStudentTokenFromFragment();
  // ...e il token che TechyWeb passa via postMessage (canale dei token, origine verificata). In un iframe
  // il parent risponde subito; senza risposta (TechyWeb vecchio) si prosegue dopo 250 ms, come oggi.
  // Non per i docenti: il loro ingresso non usa il token studente.
  // Solo se c'e' un PIN da cui entrare (altrimenti l'app non e' una sessione studente) e con attesa breve:
  // i parent vecchi non rispondono, e non si fa aspettare lo studente per un canale che non c'e'.
  if (new URLSearchParams(search).get("tl4k_role") !== "teacher" && readDerivedPin(search)) {
    await requestStudentTokenFromParent({
      timeoutMs: 250,
      // Un parent su localhost si accetta solo in sviluppo (mai in produzione).
      allowLocalhost: ((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) === true,
      // WP-13f: un parent fuori da *.techlab4kids.info (container locali, staging, IP del server) vale solo se
      // sta nell'elenco che il backend di QUESTA app serve per l'ambiente. Letto solo in quel caso.
      // Sempre COLLAB_API_BASE, MAI l'apiBase del chiamante: nessun parametro (URL, resolveCollabUrl) puo' spostare
      // da dove arriva l'elenco di fiducia.
      runtimeOrigins: () => loadRuntimeTrustedOrigins({ apiBase: COLLAB_API_BASE }).then((r) => r?.parentOrigins ?? []),
    });
  }

  const parsed = readDerivedPseudonymParams(search);
  // Compatibilita' (STUDENT_TOKEN_MODE off/issue/observe): `pseudonym_identity_id` va ancora nel body.
  // Con un token `participant` e il solo PIN (nuove versioni di TechyWeb, senza identita' in query)
  // l'ingresso funziona con il token, senza identita' dichiarata.
  const isTeacherLaunch = new URLSearchParams(search).get("tl4k_role") === "teacher";
  const tokenPin =
    !parsed && !isTeacherLaunch && getStudentTokenKind() === "participant" ? readDerivedPin(search) : null;
  if (!parsed && !tokenPin) return null;
  const derivedPin = parsed?.derivedPin ?? (tokenPin as string);

  const url = `${apiBase}/sessions/${encodeURIComponent(derivedPin)}/auto-join`;
  const options: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(parsed ? { pseudonym_identity_id: parsed.pseudonymId } : {}),
  };

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await collabFetch(url, options);
      if (response.ok) {
        const payload = await response.json();
        // Il token della sessione figlia (stesso `rsid` del padre) sostituisce quello del padre.
        if (payload && typeof payload.student_token === "string") setStudentToken(payload.student_token);
        return mapJoinData(payload);
      }
      // 5xx (server error) — retry; 4xx (client error) — no retry
      if (response.status >= 500 && attempt < retries) {
        console.warn(`[bootstrapEmbeddedStudentSession] attempt ${attempt + 1} failed (${response.status}), retrying in ${(attempt + 1) * 500}ms...`);
        await new Promise((r) => setTimeout(r, (attempt + 1) * 500));
        continue;
      }
      throw new Error(`auto-join failed (${response.status})`);
    } catch (err) {
      if (attempt < retries && err instanceof Error && !err.message.startsWith('auto-join failed (4')) {
        console.warn(`[bootstrapEmbeddedStudentSession] attempt ${attempt + 1} error:`, err);
        await new Promise((r) => setTimeout(r, (attempt + 1) * 500));
        continue;
      }
      throw err;
    }
  }
  return null;
}

export function clearEmbeddedStudentParams(url: URL): void {
  url.searchParams.delete("app_pin");
  url.searchParams.delete("derived_pin");
  url.searchParams.delete("pseudonym_id");
  url.searchParams.delete("incontro_pin");
}

export function readAnonymousEmbeddedParams(search: string): AnonymousEmbeddedParams {
  const params = new URLSearchParams(search);
  const role = params.get("tl4k_role");
  const appPin = params.get("app_pin") ?? params.get("derived_pin");
  const sessionPin = params.get("session_pin") ?? params.get("tl4k_pin");
  const pin = appPin ?? sessionPin;
  const sessionId = params.get("session_id") ?? params.get("tl4k_session_id");
  const classId = params.get("class") ?? params.get("class_id");
  return {
    pin,
    isTeacher: role === "teacher",
    sessionId: sessionId?.trim() ? sessionId : null,
    classId: classId?.trim() ? classId : null,
  };
}
